import React, { useState, useEffect } from 'react';
import { Modal, Group, Title, Button, Text, LoadingOverlay, Stack, ActionIcon, Menu, UnstyledButton, CopyButton, Tooltip, Alert } from '@mantine/core';
import AppointmentCalendar from './components/AppointmentCalendar';
import AppointmentForm from './components/AppointmentModal/AppointmentForm';
import AppointmentList from './components/AppointmentList/AppointmentList';
import './CalendarView.css';
import moment from 'moment';
import { useApp } from '../../../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';
import { IconCalendar, IconList, IconPlus, IconChevronDown, IconCopy, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import TimeBlockForm from './components/TimeBlockModal/TimeBlockForm';
import { useSalon } from '../../../context/SalonContext';
import { useCalendar } from '../../../context/CalendarContext';
import { useCalendarEvent } from '../../../context/CalendarEventContext';
import CalendarFilters from './components/CalendarFilters';

function CalendarView() {
  const navigate = useNavigate();
  const { businessInfo, isMissingSalon } = useSalon();
  const { selectedService, setSelectedDate, dateRange, selectedArtists } = useCalendar();
  const { selectedEvent, setSelectedEvent, selectedAction, setSelectedAction, events, setEvents } = useCalendarEvent();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTimeBlockModalOpen, setIsTimeBlockModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const { 
    getAppointments, 
    createAppointment, 
    updateAppointment,
    createTimeBlock,
    getTimeBlocks,
    updateTimeBlock,
    deleteTimeBlock
  } = useApp();
  const [showTrialBanner, setShowTrialBanner] = useState(true);

  const isMobile = useMediaQuery('(max-width: 768px)');
  const [isMobileListView, setIsMobileListView] = useState(isMobile);

  useEffect(() => {
    if (isMissingSalon === true) {
      navigate('/business-info');
    } 
  }, [isMissingSalon]);

  useEffect(() => {
    if (!isMissingSalon) {
      fetchAppointmentsAndTimeBlocks();

      const refreshInterval = setInterval(() => {
        fetchAppointmentsAndTimeBlocks();
      }, 5 * 60 * 1000); // 5 minutes
  
      // Cleanup function to clear interval when component unmounts
      return () => clearInterval(refreshInterval);
    }
  }, [dateRange, businessInfo]);

  const transformAppointments = (appointments) => {
    return appointments.map(apt => {
      // Combine all booked services into one string for the title
      const serviceNames = apt.booked_services.map(service => service.name).join(', ');
      const title = `${serviceNames} - ${apt.first_name} ${apt.last_name}`;

      // Convert GMT datetime to salon's timezone
      const start = moment.utc(apt.start_dt).tz(businessInfo?.timezone).format('YYYY-MM-DD HH:mm');
      const end = moment.utc(apt.end_dt).tz(businessInfo?.timezone).format('YYYY-MM-DD HH:mm');
      
      // Get the calendar_event_color from the first booked service
      const eventColor = apt.booked_services[0]?.calendar_event_color;

      return {
        id: apt.id,
        title: title,
        start: new Date(start),
        end: new Date(end),
        backgroundColor: eventColor,
        duration: (new Date(end) - new Date(start)) / (1000 * 60),
        price: apt.booked_services.reduce((acc, service) => acc + service.charged_price, 0),
        firstName: apt.first_name,
        lastName: apt.last_name,
        phone: `${apt.area_code}${apt.phone_number}`,
        email: apt.email,
        salonServiceId: apt.booked_services[0]?.salon_service_id,
        employeeName: apt.primary_staff_name,
        employeeId: apt.primary_staff_id,
        resourceId: apt.primary_staff_id, // Required for column assignment
        status: apt.status,
        customerNotes: apt.customer_notes,
        salonNotes: apt.salon_notes,
        isOptedInReminder: apt.is_opted_in_reminder,
        clientUuid: apt.client_uuid
      };
    });
  };

  const handleEditAppointment = (event) => {
    // Calculate duration from the event's start and end times
    const durationInMinutes = moment(event.end).diff(moment(event.start), 'minutes');

    setSelectedDate(moment(event.start).toDate());
    setSelectedEvent({
      ...event,
      startTime: moment(event.start).format('HH:mm'),
      endTime: moment(event.end).format('HH:mm'),
      duration: {
        hours: Math.floor(durationInMinutes / 60).toString(),
        minutes: (durationInMinutes % 60).toString()
      },
      // Include all other necessary fields
      duration: event.duration,
      firstName: event.firstName,
      lastName: event.lastName,
      phone: event.phone,
      email: event.email,
      salonServiceId: event.salonServiceId,
      employeeId: event.employeeId,
      isOptedInReminder: event.isOptedInReminder,
      clientUuid: event.clientUuid
    });
    setIsModalOpen(true);
  };

  const handleEventSelect = (event) => {
    if (event.isTimeBlock) {
      setSelectedEvent(event);
      setIsTimeBlockModalOpen(true);
      return;
    }

    setSelectedEvent(event);
    
    if (isMobile) {
      setIsMobileListView(true);
    }

    // Add a small delay to allow for state updates
    setTimeout(() => {
      const appointmentElement = document.getElementById(`appointment-${event.id}`);
      const sidebarElement = document.querySelector('.calendar-sidebar');
      
      if (appointmentElement && sidebarElement) {
        const elementTop = appointmentElement.offsetTop;
        const sidebarTop = sidebarElement.offsetTop;
        
        sidebarElement.scrollTo({
          top: elementTop - sidebarTop,
          behavior: 'smooth'
        });
      }
    }, 300);
  };

  const mapApiResponseToEvents = (response) => {
    const start_time = moment.utc(response.appointment.start_dt).tz(businessInfo?.timezone).toDate();
    const end_time = moment.utc(response.appointment.end_dt).tz(businessInfo?.timezone).toDate();
    return {
      id: response.appointment.id,
      title: `${response.booked_services.map(service => service.name).join(', ')} - ${response.appointment.first_name} ${response.appointment.last_name}`,
      price: response.booked_services.reduce((acc, service) => acc + service.charged_price, 0),
      salonServiceId: response.booked_services[0]?.salon_service_id,
      start: start_time,
      end: end_time,
      backgroundColor: response.booked_services[0]?.calendar_event_color,
      duration: (end_time - start_time) / (1000 * 60),
      firstName: response.appointment.first_name,
      lastName: response.appointment.last_name,
      phone: `${response.appointment.area_code}${response.appointment.phone_number}`,
      email: response.appointment.email,
      employeeName: response.appointment.primary_staff_name,
      employeeId: response.appointment.primary_staff_id,
      resourceId: response.appointment.primary_staff_id,
      status: response.appointment.status,
      customerNotes: response.appointment.customer_notes,
      salonNotes: response.appointment.salon_notes,
      isOptedInReminder: response.appointment.is_opted_in_reminder
    }
  };

  const mapTimeBlocksToEvent = (timeBlocks, staffId = null) => {
    return timeBlocks.map(block => {
      const start_time = moment.utc(block.start_dt).tz(businessInfo?.timezone).toDate();
      const end_time = moment.utc(block.end_dt).tz(businessInfo?.timezone).toDate();
      return {
        id: block.id,
        title: `[Block] ${block.type}${block.description ? ` - ${block.description}` : ''}`,
        start: start_time,
        end: end_time,
        resourceId: staffId ? staffId : block.staff_id,
        isTimeBlock: true,
        type: block.type,
        description: block.description,
        recurrenceParentId: block.recurrence_parent_id,
        recurrenceInterval: block.recurrence_interval,
        recurrenceUnit: block.recurrence_unit,
        recurrenceEndDate: block.recurrence_end_dt,
        status: block.status
      };
    });
  };

  const handleUpdateAppointment = async (appointmentData) => {
    try {
      const response = await updateAppointment({...appointmentData, id: appointmentData.id || selectedEvent.id});
      if (response) {
        setEvents(prevEvents => prevEvents.map(event => {
          const start = moment.utc(response.start_dt).tz('America/New_York').toDate();
          const end = moment.utc(response.end_dt).tz('America/New_York').toDate();
          return event.id === selectedEvent.id ? { 
            ...event, 
            id: event.id,
            start: start,
            end: end,
            duration: (end - start) / (1000 * 60),
            firstName: response.first_name,
            lastName: response.last_name,
            phone: `${response.area_code}${response.phone_number}`,
            email: response.email,
            salonNotes: response.salon_notes,
            isOptedInReminder: response.is_opted_in_reminder,
            clientUuid: response.client_uuid
          } : event
        }));
        setIsLoading(false);
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error('Error updating appointment:', error);
      setIsLoading(false);
    }
  };

  const handleCreateAppointment = async (appointmentData) => {
    try {
      // Call the API
      const response = await createAppointment(appointmentData);
      
      if (response) {
        // Transform the API response to match our events structure
        const newEvent = mapApiResponseToEvents(response);
  
        // Update the events state with the new appointment
        setEvents(prevEvents => [...prevEvents, newEvent]);
        
        // Close the modal or handle success
        setIsModalOpen(false);
        setIsLoading(false);
      }
    } catch (error) {
      // Handle other errors
      console.error('Error creating appointment:', error);
      setIsLoading(false);
    }
  };

  const handleAppointmentSubmit = async (appointmentData) => {
    setIsLoading(true);
    if (selectedEvent) {
      handleUpdateAppointment(appointmentData);
    } else {
      handleCreateAppointment(appointmentData);
    }
  };

  const handleNewTimeBlock = () => {
    setSelectedAction("block");
    setSelectedEvent(null);
    setIsTimeBlockModalOpen(true);
  };
  
  const handleTimeBlockSubmit = async (blockData) => {
    setIsLoading(true);
    try {
      if (selectedEvent) { // Edit existing time block
        const response = await updateTimeBlock(blockData);

        if (response) {
          const updatedEvents = mapTimeBlocksToEvent(response, selectedEvent.resourceId);
          setEvents(prevEvents => {
            // Create map of existing events by id for faster lookup
            const existingEventsMap = new Map(prevEvents.map(event => [event.id, event]));
            updatedEvents.forEach(updatedEvent => {
              existingEventsMap.set(updatedEvent.id, {...event, ...updatedEvent});
            });

            // Convert map back to array
            return Array.from(existingEventsMap.values());
          });
        }
      } else { // Create new time block
        const response = await createTimeBlock(blockData);
        const newEvents = mapTimeBlocksToEvent(response);
        
        if (blockData.recurrence_interval) {
          // If it's a recurring block, refetch all to get the series
          await fetchAppointmentsAndTimeBlocks();
        } else {
          setEvents(prevEvents => [...prevEvents, ...newEvents]);
        }
      }

      setIsTimeBlockModalOpen(false);
      setIsLoading(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error creating time block:', error);
      setIsLoading(false);
    }
  };

  const handleTimeBlockDelete = async (appliesTo) => {
    setIsLoading(true);
    try {
      await deleteTimeBlock(selectedEvent.id, appliesTo);
      
      // Remove the deleted events from state
      setEvents(prevEvents => {
        if (appliesTo === 'all') {
          // Remove all events in the series
          return prevEvents.filter(event => 
            event.recurrenceParentId !== selectedEvent.recurrenceParentId
          );
        } else if (appliesTo === 'hereafter') {
          // Remove this and all future events in the series
          const selectedDate = moment(selectedEvent.start);
          return prevEvents.filter(event => 
            event.recurrenceParentId !== selectedEvent.recurrenceParentId ||
            moment(event.start).isBefore(selectedDate)
          );
        } else {
          // Remove just this event
          return prevEvents.filter(event => event.id !== selectedEvent.id);
        }
      });

      setIsTimeBlockModalOpen(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error deleting time block:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      setIsLoading(true);
      const response = await updateAppointment({id: appointmentId, status: newStatus});
      if (response) {
        if (newStatus === 'Cancelled' || newStatus === 'Declined') {
          setEvents(prevEvents => prevEvents.filter(event => event.id !== appointmentId));
        } else {
          setEvents(prevEvents => prevEvents.map(event => 
            event.id === appointmentId ? { ...event, status: newStatus } : event
          ));
        }
        
        setIsLoading(false);
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error('Error updating appointment:', error);
      setIsLoading(false);
    }
  };

  // Filter events based on selected artists
  const filteredEvents = events.filter(event => {
    const matchesArtist = selectedArtists.includes('all') || 
      selectedArtists.includes(event.resourceId) || 
      (event.secondary_staff_id && selectedArtists.includes(event.secondary_staff_id));
    const matchesService = event.isTimeBlock || selectedService === 'all' || event.salonServiceId === selectedService;
    const isNotCancelledOrDeclined = !event.status || event.status !== 'Cancelled' && event.status !== 'Declined';
    return matchesArtist && matchesService && isNotCancelledOrDeclined;
  });

  const handleNewBooking = () => {
    setSelectedAction("appointment");
    setSelectedEvent(null);
    setIsModalOpen(true);
  };

  const fetchAppointmentsAndTimeBlocks = async () => {
    try {
      if (!businessInfo || !businessInfo?.id) {
        return;
      }

      setIsLoading(true);
      const queryParams = `start_date=${dateRange.start}&end_date=${dateRange.end}`;
      
      // Fetch appointments and time blocks in parallel
      const [appointmentsResponse, timeBlocksResponse] = await Promise.all([
        getAppointments(queryParams),
        getTimeBlocks(businessInfo.id, queryParams)
      ]);
      
      const transformedAppointments = transformAppointments(appointmentsResponse.appointments);
      // Transform time blocks
      const transformedTimeBlocks = timeBlocksResponse.records ? mapTimeBlocksToEvent(timeBlocksResponse.records) : [];
      
      setEvents([...transformedAppointments, ...transformedTimeBlocks]);
      
      if (appointmentsResponse && timeBlocksResponse) {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      setIsLoading(false);
    }
  };

  const toggleListView = () => {
    setIsMobileListView(!isMobileListView);
  };

  const shouldShowTrialBanner = () => {
    if (!businessInfo?.free_trial_end_date || businessInfo?.status === 'Active') {
      return false;
    }

    const trialEndDate = moment(businessInfo.free_trial_end_date);
    const today = moment();
    const daysUntilEnd = trialEndDate.diff(today, 'days');

    return (
      (businessInfo.status === 'Trial' || businessInfo.status === 'Inactive') && 
      daysUntilEnd <= 7 && 
      daysUntilEnd >= 0
    );
  };

  const getTrialMessage = () => {
    const daysLeft = moment(businessInfo.free_trial_end_date).diff(moment(), 'days');
    return `Your free trial ends in ${daysLeft} days. Finalize your subscription in the Settings page to continue using the platform after the trial ends.`;
  };

  return (
    <>
      <LoadingOverlay visible={isLoading} overlayBlur={2} />
      
      {shouldShowTrialBanner() && showTrialBanner && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Trial Period Ending Soon"
          withCloseButton
          onClose={() => setShowTrialBanner(false)}
        >
          {getTrialMessage()}
        </Alert>
      )}
      
      <div className="calendar-view">
        <Stack spacing="md">
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'auto 1fr auto',
            gap: '20px',
            alignItems: 'end'
          }}>
              <div className="desktop-only">
                <Title order={2}>{businessInfo?.salon_name}</Title>
                <Text size="sm">Manage your bookings, clients, schedule and more.</Text>
                <CopyButton 
                  value={`${process.env.REACT_APP_BASE_BOOKING_URI}/book?salonId=${businessInfo?.id}`} 
                  timeout={2000}
                >
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Copied!' : 'Copy link'} withArrow position="right">
                      <Group 
                        gap="xs" 
                        wrap="nowrap" 
                        onClick={copy} 
                        style={{ cursor: 'pointer' }}
                        w="fit-content"
                      >
                        <Text size="sm" c="blue" td="underline">
                          Copy booking link
                        </Text>
                        <ActionIcon 
                          color={copied ? 'teal' : 'gray'} 
                          variant="subtle" 
                          size="sm"
                        >
                          {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                        </ActionIcon>
                      </Group>
                    </Tooltip>
                  )}
                </CopyButton>
              </div>

            <CalendarFilters isMobile={isMobile} />
            { isMobile && 
              <div className="toggle-icon-container">
                {isMobileListView ? (
                  <IconCalendar
                    className="calendar-toggle"
                    onClick={toggleListView}
                    size={30}
                    style={{ cursor: 'pointer' }}
                  />
                ) : (
                  <IconList
                    className="calendar-toggle"
                    onClick={toggleListView}
                    size={30}
                    style={{ cursor: 'pointer' }}
                  />
                )}
              </div>
            }

            {/* Hide button on mobile */}
            <Menu shadow="md" width={selectedAction === "appointment" ? 222 : 180} position="bottom-end">
              <Group gap="1px" wrap="nowrap">
                <Button 
                  className="desktop-only"
                  color="violet"
                  size="md"
                  onClick={selectedAction === "appointment" ? handleNewBooking : handleNewTimeBlock}
                  style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                >
                  {selectedAction === "appointment" ? "New Appointment" : "New Time Block"}
                </Button>
                <Menu.Target>
                  <UnstyledButton 
                    className="desktop-only theme-darken-on-hover"
                    style={{
                      borderLeft: '1px solid rgba(255,255,255,0.3)',
                      width: '40px',
                      height: '42px',
                      textAlign: 'center',
                      borderTopRightRadius: '8px',
                      borderBottomRightRadius: '8px',
                      color: 'white',
                      paddingRight: '2px'
                    }}
                  >
                    <IconChevronDown size={16} style={{ verticalAlign: 'middle' }} />
                  </UnstyledButton>
                </Menu.Target>
              </Group>

              <Menu.Dropdown className="desktop-only add-block-menu" mt="-4px" style={{border: '1px solid var(--mantine-color-violet-filled)'} }>
                <Menu.Item 
                  onClick={selectedAction === "block" ? handleNewBooking : handleNewTimeBlock}
                  style={{
                    padding: '8px 16px',
                    height: '36px',
                    alignItems: 'center'
                  }}
                >
                  <Text className="white-text">{selectedAction === "block" ? "New Appointment" : "Add Time Block"}</Text>
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>

          <div className="calendar-container">
            <div className={`calendar-main ${isMobileListView ? 'hidden' : ''}`}>
              <AppointmentCalendar
                events={filteredEvents}
                onSelectEvent={handleEventSelect}
              />
            </div>
            
            <div className={`calendar-sidebar ${isMobileListView ? 'active' : ''}`}>
              <AppointmentList 
                events={filteredEvents}
                onClickEditAppointment={handleEditAppointment}
                onStatusChange={handleStatusChange}
                selectedEvent={selectedEvent}
              />
            </div>
          </div>

          <Modal
            opened={isModalOpen}
            onClose={handleModalClose}
            title={selectedEvent ? "Edit Appointment" : "Add Appointment"}
            size="lg"
            centered
          >
            <AppointmentForm
              initialData={selectedEvent}
              onSubmit={handleAppointmentSubmit}
              onCancel={(appointment) => handleStatusChange(appointment.id, 'Cancelled')}
              isEdit={!!selectedEvent}
            />
          </Modal>

          <Modal
            opened={isTimeBlockModalOpen}
            onClose={() => {
              setIsTimeBlockModalOpen(false);
              setSelectedEvent(null);
            }}
            title={selectedEvent ? "Edit Time Block" : "Add Time Block"}
            size="md"
            centered
          >
            <TimeBlockForm
              initialData={selectedEvent}
              onSubmit={handleTimeBlockSubmit}
              onDelete={handleTimeBlockDelete}
              onCancel={() => {
                setIsTimeBlockModalOpen(false);
                setSelectedEvent(null);
              }}
              isEdit={!!selectedEvent}
              isLoading={isLoading}
            />
          </Modal>
        </Stack>
           
        <div className="mobile-only">
          <Menu position="top">
            <Menu.Target>
              <ActionIcon
                className="floating-action-button"
                variant="filled"
                color="violet"
                size="48px"
                radius="xl"
                onClick={() => {}}
              >
                <IconPlus size={36} />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item 
                onClick={() => handleNewBooking()}
                style={{ padding: '12px 16px', marginBottom: '8px' }}
              >
                New Appointment
              </Menu.Item>
              <Menu.Item 
                onClick={() => handleNewTimeBlock()}
                style={{ padding: '12px 16px' }}
              >
                Add Time Block
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      </div>
    </>
  );
}

export default CalendarView; 